/* eslint-disable no-await-in-loop */
import { devLog, alwaysLog } from '@/utils/logger';
import { googleAuthService } from '@/services/GoogleAuthService';

const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';

export interface DriveUserInfo {
  user?: {
    displayName?: string
    emailAddress?: string
  }
  storageQuota?: {
    limit?: string
    usage?: string
  }
}

export interface DriveFile {
  id: string
  name: string
  mimeType?: string
  size?: string
  parents?: string[]
  appProperties?: Record<string, string>
}

export interface DriveFileListResponse {
  files: DriveFile[]
  nextPageToken?: string
}

export interface ListFilesParams {
  folderId?: string
  pageSize?: number
  orderBy?: string
  fields?: string
  driveId?: string // 新增：用于访问共享云端硬盘
}

export interface SearchFilesParams {
  query: string
  pageSize?: number
  fields?: string
}

export interface SharedDrive {
  id: string
  name: string
  capabilities?: {
    canAddChildren?: boolean
    canDelete?: boolean
    canDownload?: boolean
    canEdit?: boolean
    canListChildren?: boolean
    canManageMembers?: boolean
    canReadRevisions?: boolean
    canRename?: boolean
    canShare?: boolean
  }
}

export interface SharedDriveListResponse {
  drives: SharedDrive[]
  nextPageToken?: string
}

class DriveClient {
  private getAccessTokenOrThrow(): string {
    const token = googleAuthService.getAccessToken();
    if (!token) throw new Error('未登录，无法访问Google Drive');
    return token;
  }

  private async request<T = any>(path: string, init: globalThis.RequestInit = {}): Promise<T> {
    // 确保已认证
    const authenticated = await googleAuthService.ensureAuthenticated();
    if (!authenticated) {
      // 如果未认证，表示已触发登录跳转，中断当前请求
      throw new Error('Authentication required, redirecting to login.');
    }

    const token = this.getAccessTokenOrThrow();
    const url = path.startsWith('http') ? path : `${DRIVE_API_BASE}${path}`;
    
    devLog.log(`[DriveClient] Making request to: ${url}`);
    
    const response = await fetch(url, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        ...(init.headers || {})
      }
    });

    devLog.log(`[DriveClient] Response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      
      // 捕获 401 错误，表示 Token 失效，强制重新登录
      if (response.status === 401) {
        alwaysLog.warn(`[DriveClient] Token invalidated (401) at ${url}, refresh or re-login...`);
        await googleAuthService.recoverSessionAfterUnauthorized();
        throw new Error('Session expired, redirecting to login...');
      }

      alwaysLog.error(`[DriveClient] API error: ${response.status} - ${errorText}`);
      throw new Error(`Drive API 错误 ${response.status}: ${errorText}`);
    }

    const result = await response.json() as T;
    devLog.log(`[DriveClient] Request successful`);
    return result;
  }

  // 获取用户信息和存储配额
  async getAbout(): Promise<DriveUserInfo> {
    const params = new URLSearchParams();
    params.set('fields', 'user(displayName,emailAddress),storageQuota(usage,limit)');
    return this.request<DriveUserInfo>(`/about?${params.toString()}`);
  }

  // 列出指定文件夹中的文件
  async listFiles(params: ListFilesParams = {}): Promise<DriveFileListResponse> {
    const {
      folderId = 'root',
      pageSize = 1000,
      orderBy = 'name',
      fields = 'files(id,name,mimeType,size,parents,appProperties)',
      driveId = null // 新增：用于访问共享云端硬盘
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.set('q', `'${folderId}' in parents and trashed=false`);
    queryParams.set('spaces', 'drive');
    queryParams.set('fields', fields);
    queryParams.set('pageSize', pageSize.toString());
    queryParams.set('orderBy', orderBy);

    // 如果是访问共享云端硬盘，添加corpora参数
    if (driveId && driveId !== 'root') {
      queryParams.set('corpora', 'drive');
      queryParams.set('driveId', driveId);
      queryParams.set('includeItemsFromAllDrives', 'true');
      queryParams.set('supportsAllDrives', 'true');
    }

    return this.request<DriveFileListResponse>(`/files?${queryParams.toString()}`);
  }

  // 搜索文件
  async searchFiles(params: SearchFilesParams): Promise<DriveFileListResponse> {
    const {
      query,
      pageSize = 100,
      fields = 'files(id,name,mimeType,size,parents,appProperties)'
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.set('q', `${query} and trashed=false`);
    queryParams.set('spaces', 'drive');
    queryParams.set('fields', fields);
    queryParams.set('pageSize', pageSize.toString());

    return this.request<DriveFileListResponse>(`/files?${queryParams.toString()}`);
  }

  // 获取文件信息
  async getFileInfo(fileId: string, fields?: string, supportsAllDrives = true): Promise<DriveFile> {
    const params = new URLSearchParams();
    if (fields) {
      params.set('fields', fields);
    } else {
      params.set('fields', 'id,name,mimeType,size,parents,appProperties');
    }
    
    // 添加 supportsAllDrives 参数以支持共享文件夹
    if (supportsAllDrives) {
      params.set('supportsAllDrives', 'true');
    }

    return this.request<DriveFile>(`/files/${fileId}?${params.toString()}`);
  }

  // 下载文件为Blob
  async downloadFileBlob(fileId: string): Promise<Blob> {
    // 确保已认证
    const authenticated = await googleAuthService.ensureAuthenticated();
    if (!authenticated) {
      throw new Error('Authentication required, redirecting to login.');
    }

    const token = this.getAccessTokenOrThrow();
    
    devLog.log(`[DriveClient] 开始下载文件: ${fileId}`);
    
    const MAX_RETRIES = 3;
    let lastError: any;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      // 使用AbortController来支持超时，增加到60秒
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 60000); 
      
      try {
        if (attempt > 0) {
          devLog.log(`[DriveClient] 重试下载 (${attempt + 1}/${MAX_RETRIES})...`);
        }

        const response = await fetch(`${DRIVE_API_BASE}/files/${fileId}?alt=media`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          const errorMessage = `下载失败: ${response.status} ${response.statusText}`;
          
          // 根据状态码提供更具体的错误信息
          if (response.status === 401) {
            alwaysLog.warn(`[DriveClient] Download Token invalidated (401), refresh or re-login...`);
            await googleAuthService.recoverSessionAfterUnauthorized();
            throw new Error('认证失败，正在重新登录...');
          } else if (response.status === 403) {
            throw new Error('没有权限访问此文件');
          } else if (response.status === 404) {
            throw new Error('文件不存在或已被删除');
          } else if (response.status >= 500) {
            throw new Error(`服务器错误，请稍后重试 (${response.status})`);
          } else {
            throw new Error(`${errorMessage} - ${errorText}`);
          }
        }

        const blob = await response.blob();
        
        if (!blob || blob.size === 0) {
          throw new Error('下载的文件为空');
        }
        
        devLog.log(`[DriveClient] 文件下载成功: ${fileId}, 大小: ${blob.size} bytes`);
        return blob;
      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error;
        
        // 判断是否可以重试
        let isRetryable = false;
        
        if (error instanceof Error) {
          // 超时
          if (error.name === 'AbortError') isRetryable = true;
          // 网络错误 (Failed to fetch)
          if (error.message.includes('Failed to fetch')) isRetryable = true;
          // 服务器错误
          if (error.message.includes('服务器错误')) isRetryable = true;
        } else {
          // 未知错误尝试重试
          isRetryable = true;
        }

        // 如果是最后一次尝试，或者错误不可重试，则退出循环
        if (attempt === MAX_RETRIES - 1 || !isRetryable) {
          break;
        }
        
        // 等待后重试 (1s, 2s, ...)
        const delay = 1000 * (attempt + 1);
        devLog.warn(`[DriveClient] 下载出错: ${error instanceof Error ? error.message : error}，${delay}ms 后重试...`);
        await new Promise<void>(resolve => {
          setTimeout(resolve, delay);
        });
      }
    }

    // 处理最终错误
    if (lastError instanceof Error) {
      if (lastError.name === 'AbortError') {
        throw new Error('下载超时，请检查网络连接（已重试多次）');
      }
      throw lastError;
    } else {
      throw new Error(`下载失败: ${String(lastError)}`);
    }
  }

  // 下载文件为文本
  async downloadFileText(fileId: string): Promise<string> {
    // 确保已认证
    const authenticated = await googleAuthService.ensureAuthenticated();
    if (!authenticated) {
      throw new Error('Authentication required, redirecting to login.');
    }

    const token = this.getAccessTokenOrThrow();
    
    const MAX_RETRIES = 3;
    let lastError: any;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 文本文件通常较小，30秒应该足够

      try {
        if (attempt > 0) {
          devLog.log(`[DriveClient] 重试下载文本 (${attempt + 1}/${MAX_RETRIES})...`);
        }

        const response = await fetch(`${DRIVE_API_BASE}/files/${fileId}?alt=media`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          if (response.status >= 500) {
            throw new Error(`服务器错误 (${response.status})`);
          } else if (response.status === 401) {
             // 401 不重试，直接抛出，让外层处理登录
             throw new Error('认证失败，需要重新登录');
          }
          // 4xx (e.g. 403, 404) are thrown and not retried unless specifically handled
          throw new Error(`下载失败: ${response.status} ${response.statusText} - ${errorText}`);
        }

        return await response.text();
      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error;

        // 判断是否可以重试
        let isRetryable = false;
        if (error instanceof Error) {
          if (error.name === 'AbortError') isRetryable = true;
          if (error.message.includes('Failed to fetch')) isRetryable = true;
          if (error.message.includes('服务器错误')) isRetryable = true;
        } else {
          isRetryable = true;
        }

        if (attempt === MAX_RETRIES - 1 || !isRetryable) {
          break;
        }

        const delay = 1000 * (attempt + 1);
        await new Promise<void>(resolve => {
          setTimeout(resolve, delay);
        });
      }
    }

    if (lastError instanceof Error) {
        if (lastError.name === 'AbortError') throw new Error('下载文本超时');
        throw lastError;
    }
    throw new Error(`下载文本失败: ${String(lastError)}`);
  }

  // 创建文件或文件夹
  async createFile(fileData: {
    name: string
    mimeType: string
    parents?: string[]
    appProperties?: Record<string, string>
  }): Promise<DriveFile> {
    // 确保已认证
    const authenticated = await googleAuthService.ensureAuthenticated();
    if (!authenticated) {
      throw new Error('Authentication required, redirecting to login.');
    }

    const body = {
      name: fileData.name,
      mimeType: fileData.mimeType,
      ...(fileData.parents && { parents: fileData.parents }),
      ...(fileData.appProperties && { appProperties: fileData.appProperties })
    };

    return this.request<DriveFile>('/files', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  // 获取共享云端硬盘列表
  async listSharedDrives(params: { pageSize?: number } = {}): Promise<SharedDriveListResponse> {
    const { pageSize = 100 } = params;
    
    const queryParams = new URLSearchParams();
    queryParams.set('pageSize', pageSize.toString());
    queryParams.set('fields', 'drives(id,name,capabilities),nextPageToken');

    return this.request<SharedDriveListResponse>(`/drives?${queryParams.toString()}`);
  }

  // ANCHOR: DRIVE_ENSURE_FOLDER_IN_SHARED_DRIVE_START
  async ensureFolderInSharedDrive(driveId: string, folderName: string): Promise<DriveFile> {
    const name = (folderName || '').trim() || `Project-${Date.now()}`;
    
    // 构建查询条件：查找指定名称的文件夹
    const q = [
      "mimeType = 'application/vnd.google-apps.folder'",
      `name = '${name.replace(/'/g, "\\'")}'`,
      `'${driveId}' in parents`
    ].join(' and ');
    
    const params = new URLSearchParams();
    params.set('q', `${q} and trashed=false`);
    params.set('spaces', 'drive');
    params.set('fields', 'files(id,name,mimeType,parents)');
    params.set('corpora', 'drive');
    params.set('driveId', driveId);
    params.set('includeItemsFromAllDrives', 'true');
    params.set('supportsAllDrives', 'true');

    const found = await this.request<DriveFileListResponse>(`/files?${params.toString()}`);
    if (found.files && found.files.length) {
      devLog.log(`[DriveClient] 找到现有文件夹: ${name}, ID: ${found.files[0].id}`);
      return found.files[0];
    }

    // 创建文件夹（共享盘需 supportsAllDrives=true）
    devLog.log(`[DriveClient] 创建新文件夹: ${name} 在共享盘 ${driveId}`);
    return this.request<DriveFile>('/files?supportsAllDrives=true', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        mimeType: 'application/vnd.google-apps.folder',
        parents: [driveId],
        appProperties: { app: 'T4-edit', type: 'project' }
      })
    });
  }
  // ANCHOR: DRIVE_ENSURE_FOLDER_IN_SHARED_DRIVE_END

  /**
   * 在指定父文件夹下查找或创建子文件夹（共享盘 / 普通盘均可用）。
   */
  async ensureChildFolder(params: {
    parentId: string
    folderName: string
    driveId?: string
  }): Promise<DriveFile> {
    const name = (params.folderName || '').trim();
    if (!name) throw new Error('folderName required');
    const parentId = params.parentId;
    const q = [
      "mimeType = 'application/vnd.google-apps.folder'",
      `name = '${name.replace(/'/g, "\\'")}'`,
      `'${parentId}' in parents`,
      'trashed=false'
    ].join(' and ');

    const listParams = new URLSearchParams();
    listParams.set('q', q);
    listParams.set('spaces', 'drive');
    listParams.set('fields', 'files(id,name,mimeType,parents)');
    listParams.set('pageSize', '10');
    listParams.set('supportsAllDrives', 'true');
    listParams.set('includeItemsFromAllDrives', 'true');
    if (params.driveId) {
      listParams.set('corpora', 'drive');
      listParams.set('driveId', params.driveId);
    }

    const found = await this.request<DriveFileListResponse>(`/files?${listParams.toString()}`);
    if (found.files?.length) {
      return found.files[0];
    }

    devLog.log(`[DriveClient] 创建子文件夹: ${name} under ${parentId}`);
    return this.request<DriveFile>('/files?supportsAllDrives=true', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        mimeType: 'application/vnd.google-apps.folder',
        parents: [parentId],
        appProperties: { app: 'T4-edit', type: 'layout' }
      })
    });
  }

  // ANCHOR: DRIVE_UPLOAD_MULTIPART_START
  async uploadFileMultipart(params: { name: string; mimeType: string; parents: string[]; blob: Blob }): Promise<DriveFile> {
    // 确保已认证
    const authenticated = await googleAuthService.ensureAuthenticated();
    if (!authenticated) throw new Error('Authentication required, redirecting to login.');
    const token = this.getAccessTokenOrThrow();

    const metadata = {
      name: params.name,
      mimeType: params.mimeType,
      parents: params.parents
    };

    const boundary = `t4-${Math.random().toString(36)
.slice(2)}`;
    const delimiter = `--${boundary}`;
    const closeDelimiter = `--${boundary}--`;

    const bodyParts = [
      `${delimiter}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n`,
      `${delimiter}\r\nContent-Type: ${params.mimeType}\r\n\r\n`,
      new Uint8Array(await params.blob.arrayBuffer()),
      `\r\n${closeDelimiter}`
    ];

    const response = await fetch(`${DRIVE_UPLOAD_BASE}/files?uploadType=multipart&supportsAllDrives=true`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`
      },
      body: new Blob(bodyParts as any, { type: `multipart/related; boundary=${boundary}` })
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      if (response.status === 401) {
        await googleAuthService.recoverSessionAfterUnauthorized();
        throw new Error('Session expired, redirecting to login...');
      }
      throw new Error(`Drive multipart upload error ${response.status}: ${text}`);
    }
    return response.json() as Promise<DriveFile>;
  }
  // ANCHOR: DRIVE_UPLOAD_MULTIPART_END

  // 删除文件
  async deleteFile(fileId: string): Promise<void> {
    // 确保已认证
    const authenticated = await googleAuthService.ensureAuthenticated();
    if (!authenticated) throw new Error('Authentication required, redirecting to login.');
    const token = this.getAccessTokenOrThrow();

    const response = await fetch(`${DRIVE_API_BASE}/files/${fileId}?supportsAllDrives=true`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      if (response.status === 401) {
        await googleAuthService.recoverSessionAfterUnauthorized();
        throw new Error('Session expired, redirecting to login...');
      }
      throw new Error(`Drive delete file error ${response.status}: ${text}`);
    }
  }

  // 重命名文件
  async renameFile(fileId: string, newName: string): Promise<DriveFile> {
    // 确保已认证
    const authenticated = await googleAuthService.ensureAuthenticated();
    if (!authenticated) throw new Error('Authentication required, redirecting to login.');
    const token = this.getAccessTokenOrThrow();

    devLog.log(`[DriveClient] 重命名文件: fileId=${fileId}, newName=${newName}`);

    // 使用 PATCH 方法更新文件元数据（只更新 name）
    const response = await fetch(`${DRIVE_API_BASE}/files/${fileId}?supportsAllDrives=true`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: newName
      })
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      const errorMsg = `Drive rename file error ${response.status}: ${text}`;
      alwaysLog.error(`[DriveClient] ${errorMsg}`);
      throw new Error(errorMsg);
    }

    const renamedFile = await response.json() as DriveFile;
    devLog.log(`[DriveClient] 文件重命名成功:`, renamedFile);
    return renamedFile;
  }

  // 更新文件内容（覆盖现有文件）
  async updateFileContent(fileId: string, blob: Blob, mimeType = 'application/json'): Promise<DriveFile> {
    // 确保已认证
    const authenticated = await googleAuthService.ensureAuthenticated();
    if (!authenticated) throw new Error('Authentication required, redirecting to login.');
    const token = this.getAccessTokenOrThrow();

    devLog.log(`[DriveClient] 更新文件内容: fileId=${fileId}, size=${blob.size}, mimeType=${mimeType}`);

    // 使用 uploadType=media 来更新文件内容
    // 注意：使用 PATCH 方法更新文件内容，而不是 POST
    const response = await fetch(`${DRIVE_UPLOAD_BASE}/files/${fileId}?uploadType=media&supportsAllDrives=true`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': mimeType
      },
      body: blob
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      if (response.status === 401) {
        await googleAuthService.recoverSessionAfterUnauthorized();
        throw new Error('Session expired, redirecting to login...');
      }
      const errorMsg = `Drive update file content error ${response.status}: ${text}`;
      alwaysLog.error(`[DriveClient] ${errorMsg}`);
      throw new Error(errorMsg);
    }

    devLog.log(`[DriveClient] 文件内容更新成功: fileId=${fileId}`);
    
    // 使用 uploadType=media 时，响应只包含基本文件信息
    // 需要再次调用 getFileInfo 获取完整信息（包括 parents）
    try {
      const fullFileInfo = await this.getFileInfo(fileId, undefined, true);
      devLog.log(`[DriveClient] 获取完整文件信息成功:`, fullFileInfo);
      return fullFileInfo;
    } catch (error) {
      devLog.warn('[DriveClient] 获取完整文件信息失败，尝试解析响应:', error);
      // 如果获取完整信息失败，尝试从响应中解析
      try {
        const updatedFile = await response.json() as DriveFile;
        return updatedFile;
      } catch (parseError) {
        alwaysLog.error('[DriveClient] 解析响应失败:', parseError);
        throw new Error('更新文件成功，但无法获取文件信息');
      }
    }
  }

  private async resolvePrimaryIdViaInferFolder(
    candidateFiles: DriveFile[],
    projectName: string,
    fileName: string,
    driveId?: string
  ): Promise<string | null> {
    const projectQuery = [
      "mimeType = 'application/vnd.google-apps.folder'",
      `name = '${projectName.replace(/'/g, '\\\'')}'`,
      'trashed=false'
    ].join(' and ');

    const projectParams = new URLSearchParams();
    projectParams.set('q', projectQuery);
    projectParams.set('spaces', 'drive');
    projectParams.set('fields', 'files(id,name)');
    projectParams.set('pageSize', '10');

    if (driveId && driveId !== 'root') {
      projectParams.set('corpora', 'drive');
      projectParams.set('driveId', driveId);
      projectParams.set('includeItemsFromAllDrives', 'true');
      projectParams.set('supportsAllDrives', 'true');
    }

    const projectResult = await this.request<DriveFileListResponse>(`/files?${projectParams.toString()}`);
    const projectFolderId = projectResult.files?.[0]?.id;
    if (!projectFolderId) return null;

    const infersQuery = [
      "mimeType = 'application/vnd.google-apps.folder'",
      "name = 'infers'",
      `'${projectFolderId}' in parents`,
      'trashed=false'
    ].join(' and ');

    const infersParams = new URLSearchParams();
    infersParams.set('q', infersQuery);
    infersParams.set('spaces', 'drive');
    infersParams.set('fields', 'files(id,name)');
    infersParams.set('pageSize', '10');

    if (driveId && driveId !== 'root') {
      infersParams.set('corpora', 'drive');
      infersParams.set('driveId', driveId);
      infersParams.set('includeItemsFromAllDrives', 'true');
      infersParams.set('supportsAllDrives', 'true');
    }

    const infersResult = await this.request<DriveFileListResponse>(`/files?${infersParams.toString()}`);
    const infersFolderId = Array.isArray(infersResult.files) && infersResult.files.length > 0
      ? infersResult.files[0].id
      : null;
    if (!infersFolderId) return null;

    return this.pickPrimaryInInferParents(candidateFiles, infersFolderId, projectName, fileName);
  }

  private pickPrimaryInInferParents(
    files: DriveFile[],
    infersFolderId: string,
    projectName: string,
    fileName: string
  ): string | null {
    for (const file of files) {
      if (file.parents?.includes(infersFolderId)) {
        devLog.log(`[DriveClient] 在项目 ${projectName}/infers 中找到主要文件: ${fileName}, ID: ${file.id}`);
        return file.id;
      }
    }
    return null;
  }

  // 通过项目名称和文件名查询DriveID，返回所有重名文件的ID
  async getFileIdByProjectAndNameInInfer(projectName: string, fileName: string, driveId?: string): Promise<{ primaryId: string | null; candidateIds: string[] }> {
    try {
      // 构建查询条件：查找指定项目文件夹下的指定文件
      const q = [
        `name = '${fileName.replace(/'/g, '\\\'')}'`,
        'trashed=false'
      ].join(' and ');
      
      const params = new URLSearchParams();
      params.set('q', q);
      params.set('spaces', 'drive');
      params.set('fields', 'files(id,name,mimeType,parents)');
      params.set('pageSize', '100'); // 增加结果数量以获取更多候选文件
      
      // 如果是访问共享云端硬盘，添加相关参数
      if (driveId && driveId !== 'root') {
        params.set('corpora', 'drive');
        params.set('driveId', driveId);
        params.set('includeItemsFromAllDrives', 'true');
        params.set('supportsAllDrives', 'true');
      }

      const result = await this.request<DriveFileListResponse>(`/files?${params.toString()}`);
      
      if (!result.files || result.files.length === 0) {
        devLog.log(`[DriveClient] 未找到文件: ${fileName}`);
        return { primaryId: null, candidateIds: [] };
      }

      // 收集所有找到的文件ID
      const allFileIds = result.files.map(file => file.id);
      let primaryId: string | null = null;
      let candidateIds: string[] = [];

      // 如果有多个同名文件，尝试通过项目名称和infers文件夹进一步筛选
      if (result.files.length > 1) {
        devLog.log(`[DriveClient] 找到多个同名文件，尝试通过项目名称和infers文件夹筛选: ${fileName}`);
        const picked = await this.resolvePrimaryIdViaInferFolder(result.files, projectName, fileName, driveId);
        if (picked) {
          primaryId = picked;
        }
      }
      
      // 如果没有找到主要文件，使用第一个匹配的文件作为主要文件
      if (!primaryId) {
        primaryId = result.files[0].id;
        devLog.log(`[DriveClient] 使用第一个匹配的文件作为主要文件: ${fileName}, ID: ${primaryId}`);
      }
      
      // 构建候选文件ID列表，主要文件ID放在第一位，其他文件ID按顺序排列
      candidateIds = [primaryId, ...allFileIds.filter(id => id !== primaryId)];
      
      devLog.log(`[DriveClient] 找到文件: ${fileName}, 主要ID: ${primaryId}, 候选ID列表: [${candidateIds.join(', ')}]`);
      return { primaryId, candidateIds };
    } catch (error) {
      devLog.error(`[DriveClient] 查询文件ID失败: ${fileName}`, error);
      return { primaryId: null, candidateIds: [] };
    }
  }
}

export const driveClient = new DriveClient();